/* eslint-disable max-lines, simple-import-sort/imports, react-hooks/exhaustive-deps */
import {
  Box,
  Divider,
  TypographyProps,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import { usePathname } from 'next/navigation';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { AbgTypography, formatMoney, paletteBase } from '@abg/kit-base';
import { usePageContext } from '@abg/web-core/context/page/client-provider';

import { AmazonBanner } from '../../atoms/AmazonBanner';
import { AemImage } from '../../molecules/AemImage';
import { useAncillariesDialogContext } from '../../organisms/Ancillaries/contexts';
import { DiscountSection } from '../../organisms/CheckoutModule/molecules/rental-summary/DiscountSection';
import { getPriceTypeData } from '../../organisms/CheckoutModule/molecules/would-you-like-to-pay/would-you-like-to-pay.utils';
import { useIsAmazonCashbackApply } from '../../organisms/VehicleList/organisms/VehicleLists/use-is-amazon-cashback-apply';
import { useTranslationContext } from '../../shared/context';
import { AncillariesI18nKeys } from '../../shared/types';
import { bookingFlowStoreSelector } from '../../store/index';
import { preCheckStoreSelector } from '../../store/pre-check/pre-check.store';
import { useIsUpgradeFlow } from '../../hooks/use-is-upgrade-flow';
import { PricingSummaryData } from '../AncillariesBundles/AncillariesBundle';

import { CarInfoDetails } from './components/CarInfoDetails';
import { CarInfoHeader } from './components/CarInfoHeader';
import { CarInfoSummary } from './components/CarInfoSummary';

import frequentFlyerImage from '../../organisms/CheckoutModule/molecules/earn-points/frequent-flyer.png';
import { CarInfoTaxes } from './components/CarInfoTaxes';
import { LineItem } from './components/CarPointsSummary/points-accordion.types';
import PointsAccordionSection from './components/CarPointsSummary/PointsAccordion';
import { RateTermsAccordion } from './components/RateTermsAccordion';
import { dividerStyling } from './components/RateTermsAccordion/RateTermsAccordion.styling';
import { getLimitedMilesText } from './components/utils/limited-miles-text/limited-miles-text';
import { useShowDiscountSection } from './hooks/use-show-discount-section';
import { useUnpricedExtrasNotes } from './hooks/use-unpriced-extras-notes';
import {
  activeSummaryClass,
  CustomMuiAccordionClass,
  disableSelectionClass,
  MuiAccordionElevation,
  PANELS,
} from './car-info-accordion.constants';
import {
  createRegionAriaReference,
  getAncillariesCostList,
  makeSavingsList,
} from './car-info-accordion.helpers';
import {
  ActiveSummaryType,
  CarInfoAccordionProps,
  CarInfoSavingsParams,
  CostListType,
  onChangeHandler,
} from './car-info-accordion.types';
import {
  carInfoAccordionStyling,
  carInfoAccordionWrapperStyling,
  carInfoSummaryStyling,
  checkoutMobileAccordionStyling,
  qantasBannerContainer,
  qantasBannerText,
  qantasBannerSubText,
} from './CarInfoAccordion.styling';

const MOBILE_DOUBLE_FIRE_THRESHOLD_MS = 300;

// eslint-disable-next-line complexity
export const CarInfoAccordion: FC<CarInfoAccordionProps> = ({
  duration,
  vehicleImage,
  vehicleType,
  vehicleDescription,
  styleParts,
  parts,
  accordionPanelImageStyle,
  showRateTerms,
  hideRateTerms,
  pricingData,
  pricingDataLoading,
  currency,
  isCheckoutMobile,
  hideAmazonBanner,
  recalculatePrices,
  handlePayNowChange,
  onCostRemove,
  enableQantasFrequentFlyerBanner,
  isPayNowChecked,
  isFromFooterPopover,
}) => {
  const [activeSummary, setActiveSummary] = useState<ActiveSummaryType>(
    {} as ActiveSummaryType,
  );
  const [pointsItem, setPointsItem] = useState<LineItem[]>([]);

  const ancillariesDialogContextAPI = useAncillariesDialogContext();
  const { t, i18nKeys } = useTranslationContext<AncillariesI18nKeys>();
  const pageContext = usePageContext();

  const getState = bookingFlowStoreSelector.use.getState();
  const updateRate = bookingFlowStoreSelector.use.updateRate();
  const vehiclePayType = bookingFlowStoreSelector.use.vehiclePayType();
  const { isUpgradeFlow } = useIsUpgradeFlow();

  const enableDiscountSection =
    pageContext?.features?.['feature.enableDiscountSection'] ?? false;
  const pathname = usePathname();
  const restrictedPage = ['protectioncoverage', 'addons'];
  const isRestrictedPage = restrictedPage.some((page) =>
    pathname?.includes(page),
  );

  const showDiscountSection = useShowDiscountSection(vehiclePayType);
  const normalizedVehiclePayType = String(vehiclePayType ?? '').toLowerCase();
  const isPayNowSelected =
    normalizedVehiclePayType === 'paynow' ||
    normalizedVehiclePayType === 'pay_now';
  const unpricedExtrasNotes = useUnpricedExtrasNotes(pricingData);
  const isEmea = pageContext?.region === 'emea';
  const isApac = pageContext?.region === 'apac';

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const pickupCountryCode = bookingFlowStoreSelector.use.pickupCountryCode?.();
  const residencyValue = bookingFlowStoreSelector.use.residencyValue?.();
  const protectionsConfig = bookingFlowStoreSelector.use.protectionsConfig?.();
  const addOnsConfig = bookingFlowStoreSelector.use.addOnsConfig?.();

  const isAvisFirst =
    bookingFlowStoreSelector.use.isAvisFirst() ||
    preCheckStoreSelector.use.preCheckInfo()?.reservationInformation
      ?.brandInformation?.subBrand === 'Q';

  const avisFirstVehicleImage =
    isAvisFirst && vehicleImage
      ? vehicleImage.replace(
          /(?:-white)?(?:_featured)?\.png$/,
          '-white_featured.png',
        )
      : vehicleImage;

  const isAvisFirstVehicleSelected =
    bookingFlowStoreSelector.use.isAvisFirst() === true;

  const shouldHideAvisFirstPrepayStrikeThrough =
    isAvisFirstVehicleSelected && (isPayNowSelected || isUpgradeFlow);

  const vehicleModelDescription =
    bookingFlowStoreSelector.use.vehicleModelDescription?.();

  const showQantasPointsBannerInRateTermsCheckout =
    pickupCountryCode?.toUpperCase() === 'AU' &&
    residencyValue?.toUpperCase() === 'AU' &&
    enableQantasFrequentFlyerBanner;

  const qantasPoints = pricingData?.totals?.quantasPoints ?? 0;

  const isAmazonCashbackApply = useIsAmazonCashbackApply();

  useEffect(() => {
    // Initialize activeSummary with protections-addons expanded by default
    setActiveSummary((prevStyles) => {
      return { ...prevStyles, [PANELS.SECOND]: activeSummaryClass };
    });

    // Initialize activeSummary with taxes-fees expanded if tax total > 0
    if ((pricingData?.totals?.taxAndFreeTotal || 0) > 0 && !isApac) {
      setActiveSummary((prevStyles) => {
        return { ...prevStyles, [PANELS.FOURTH]: activeSummaryClass };
      });
    }

    if ((pricingData?.totals?.totalPoints || 0) > 0) {
      setActiveSummary((prevStyles) => {
        return { ...prevStyles, [PANELS.POINTS]: activeSummaryClass };
      });
    }

    if (pricingData) {
      const paidWithPoints = (pricingData.addOnItems ?? []).filter(
        (item) => item.payWithPoints === true,
      );
      const paidWithPointsAndFreeDay = [
        ...paidWithPoints,
        ...(pricingData?.freedayItem
          ? [
              {
                description:
                  pricingData.freedayItem.quantity + ' day of free rental',
                ...pricingData.freedayItem,
              },
            ]
          : []),
      ];
      setPointsItem(paidWithPointsAndFreeDay);
    }
  }, [pricingData]);

  const onPayNowChange = (value: boolean) => {
    const { priceType, priceRateCode } = getPriceTypeData(value, getState());

    // we don't need to update the rate if we are on the checkout
    // only call the handler from the Checkout component
    if (handlePayNowChange) {
      handlePayNowChange(value);
    }

    updateRate({
      priceRateCode,
      payType: vehiclePayType,
      priceType,
    });

    if (!handlePayNowChange) {
      recalculatePrices?.(null);
    }
  };

  const lastToggleReference = useRef<{ panel: string; time: number } | null>(
    null,
  );

  const onChange: onChangeHandler = (panel) => (_event, expanded) => {
    const now = Date.now();
    // Deduplicate: MUI ButtonBase fires onChange twice on mobile (touchend + synthetic click).
    // Skip if the same panel was toggled within the threshold window.
    if (
      lastToggleReference.current?.panel === panel &&
      now - lastToggleReference.current.time < MOBILE_DOUBLE_FIRE_THRESHOLD_MS
    ) {
      return;
    }
    lastToggleReference.current = { panel, time: now };
    setActiveSummary((prevStyles) => ({
      ...prevStyles,
      [panel]: expanded ? activeSummaryClass : null,
    }));
  };

  const hasSavings = useMemo(() => {
    return Number(pricingData?.savings?.totalSavings || 0) !== 0;
  }, [pricingData?.savings]);

  const savingsList: CarInfoSavingsParams[] = useMemo(
    () => makeSavingsList(pricingData as PricingSummaryData, t, i18nKeys),
    [pricingData, t, i18nKeys],
  );

  const hasTaxesFeesCostList = useMemo(() => {
    return Number(pricingData?.taxAndFeeItems?.length) > 0;
  }, [pricingData?.taxAndFeeItems]);

  const isAvisInclusive =
    (pricingData?.protectionBundle as { emeaCode?: string })?.emeaCode ===
    'CPI';

  const useRentalOptionsOnly = isAvisInclusive && isEmea;

  const protectionAddOnTotalPrice = (
    useRentalOptionsOnly
      ? [pricingData?.totals?.rentalOptionsTotal || 0]
      : [
          pricingData?.totals?.protectionTotal || 0,
          pricingData?.totals?.addOnTotal || 0,
        ]
  )
    .filter((value) => typeof value === 'number' || typeof value === 'string')
    .reduce((accumulator: number, value) => accumulator + Number(value), 0);

  const protectionsAddonsList = useMemo(
    () =>
      [
        ...(pricingData?.protectionItems || []),
        ...(pricingData?.addOnItems || []),
      ] as CostListType[],
    [pricingData],
  );

  const mobileVariants: {
    headerVariant?: TypographyProps['variant'];
    durationVariant?: TypographyProps['variant'];
  } = isCheckoutMobile
    ? {
        headerVariant: 'bodySmallMedium',
        durationVariant: 'bodySmallRegular',
      }
    : {};

  const accordionSx = {
    ...carInfoAccordionStyling.accordion,
    ...(isCheckoutMobile ? checkoutMobileAccordionStyling : {}),
  };

  const periodFreeMiles = pricingData?.rateTerms?.periodFreeMiles;

  const limitedMilesTextLabel = getLimitedMilesText(
    t,
    currency,
    periodFreeMiles,
  );

  const ancillariesCostList = useMemo(() => {
    const list = getAncillariesCostList(pricingData);

    // AF Standalone Upgrade (EMEA): resolve display names from AEM location config
    // stored in bookingFlowStore. This matches how the review cards resolve names.
    // Handles race condition where populateWithTitle may not have resolved names yet.
    if (isUpgradeFlow && isEmea && isAvisFirst) {
      // Build code→name map from AEM protections bundles + individual items
      const nameMap: Record<string, string> = {};

      // Protection bundles (CP1→Standard, CP2→Vehicle Cover, etc.)
      for (const bundle of protectionsConfig?.data?.protectionBundleList
        ?.items ?? []) {
        if (bundle.code && bundle.bundleName) {
          nameMap[bundle.code] = bundle.bundleName;
        }
        for (const item of bundle.includedProtections ?? []) {
          if (item.code && item.name) nameMap[item.code] = item.name;
        }
      }
      // Protection individual items
      for (const item of protectionsConfig?.data?.protectionReferencesList
        ?.items?.[0]?.protectionList ?? []) {
        if (item.code && item.name) nameMap[item.code] = item.name;
      }
      // Add-on items from all categories
      for (const category of addOnsConfig?.data?.addOnCategoryList?.items ??
        []) {
        for (const item of category.addOnList ?? []) {
          if (item.code && item.name) nameMap[item.code] = item.name;
        }
      }

      return list.map((item) =>
        item.code && nameMap[item.code] && item.description === item.code
          ? { ...item, description: nameMap[item.code] }
          : item,
      );
    }

    return list;
  }, [
    pricingData,
    isUpgradeFlow,
    isEmea,
    isAvisFirst,
    protectionsConfig,
    addOnsConfig,
  ]);

  return (
    <Box
      sx={carInfoAccordionWrapperStyling({
        ...styleParts?.wrapper,
        ...(isCheckoutMobile ? { overflowY: 'visible' } : {}),
      })}
      data-testid="rental-summary-wrapper"
    >
      {!isCheckoutMobile && (
        <Box sx={carInfoAccordionStyling.headerContainerGutter}>
          <Box
            sx={{
              ...(isAvisFirst && {
                background: isFromFooterPopover
                  ? '#000'
                  : 'radial-gradient(124.57% 33.08% at 3.61% 59.8%, #D4002A 0%, #000 100%)',
                backdropFilter: 'blur(40px)',
                margin: '-12px -24px 12px -24px',
                padding: '12px 24px',
              }),
            }}
          >
            <CarInfoHeader
              styleParts={
                isAvisFirst && styleParts
                  ? {
                      ...styleParts,
                      header: {
                        ...styleParts.header,
                        color: '#FFFFFF',
                      },
                    }
                  : styleParts
              }
              vehicleType={vehicleType}
              vehicleDescription={vehicleDescription || vehicleModelDescription}
              vehicleImage={avisFirstVehicleImage}
              isAvisFirst={isAvisFirst}
              accordionPanelImageStyle={accordionPanelImageStyle}
            />
          </Box>

          {/* Pickup & Dropoff Section */}
          {parts?.pickupDropSection?.()}
        </Box>
      )}
      {/* Vehicle price details */}
      <MuiAccordion
        sx={accordionSx}
        // If the accordion doesn't have content to show, disable the click
        className={`${CustomMuiAccordionClass} ${disableSelectionClass}`}
        disableGutters
        elevation={MuiAccordionElevation}
        square
        ref={createRegionAriaReference(t(i18nKeys.vehicleTotalRateLabel))}
        onChange={onChange(PANELS.FIRST)}
        data-testid="category-expand-button-vehicle-total-rate"
      >
        <CarInfoSummary
          currency={currency}
          headerVariant="bodySmallMedium"
          durationVariant="bodySmallRegular"
          testId="rental-summary-vehicle-total-rate"
          headerText={t(i18nKeys.vehicleTotalRateLabel)}
          activeSummary={activeSummary[PANELS.FIRST]}
          hasExpandIcon={false}
          duration={duration}
          recentCost={pricingData?.totals?.netSubtotal}
          previousCost={
            shouldHideAvisFirstPrepayStrikeThrough
              ? undefined
              : pricingData?.totals?.grossSubtotal
          }
          carInfoStyling={{
            wrapperStyles: styleParts?.vehicleRateAndProtections,
            headerTextStyles: styleParts?.vehicleTotalRate,
            recentCostStyles: carInfoSummaryStyling.recentCostStyles,
            previousCostStyles: styleParts?.vehiclePrevRateValue,
          }}
          styleParts={styleParts}
          isCheckoutMobile={isCheckoutMobile}
          hasPricingData={!!pricingData}
        />
      </MuiAccordion>
      {pricingData?.rateTerms?.unlimitedMilage && (
        <AbgTypography
          component="p"
          variant="bodySmallRegular"
          sx={carInfoAccordionStyling?.subText}
        >
          {t(i18nKeys.freeMilesInclude)}
        </AbgTypography>
      )}
      {!pricingData?.rateTerms?.unlimitedMilage && limitedMilesTextLabel && (
        <AbgTypography
          component="p"
          variant="bodySmallRegular"
          sx={carInfoAccordionStyling?.subText}
        >
          {limitedMilesTextLabel}
        </AbgTypography>
      )}
      {/* Protections and Addons price details */}
      <MuiAccordion
        sx={accordionSx}
        // If the accordion doesn't have content to show, disable the click
        className={`${CustomMuiAccordionClass} ${
          protectionsAddonsList && protectionsAddonsList.length === 0
            ? disableSelectionClass
            : ''
        }`}
        disableGutters
        elevation={MuiAccordionElevation}
        square
        ref={createRegionAriaReference(t(i18nKeys.protectionsAndAddonsLabel))}
        expanded={!!activeSummary[PANELS.SECOND]}
        onChange={onChange(PANELS.SECOND)}
        data-testid="category-expand-button-protections-addons"
      >
        <CarInfoSummary
          currency={currency}
          testId="rental-summary-protection-addons"
          hasExpandIcon={protectionsAddonsList.length > 0}
          activeSummary={activeSummary[PANELS.SECOND]}
          headerText={t(i18nKeys.protectionsAndAddonsLabel)}
          recentCost={protectionAddOnTotalPrice}
          headerVariant="bodySmallMedium"
          carInfoStyling={{
            wrapperStyles: styleParts?.vehicleRateAndProtections,
            headerTextStyles: styleParts?.protectionAndAddonsLabel,
            recentCostStyles: carInfoSummaryStyling.recentCostStyles,
          }}
          styleParts={styleParts}
          {...mobileVariants}
          isCheckoutMobile={isCheckoutMobile}
          hasPricingData={!!pricingData}
        />

        <CarInfoDetails
          currency={currency}
          costList={ancillariesCostList}
          onCostRemove={isUpgradeFlow && isEmea ? undefined : onCostRemove}
          stylePart={styleParts?.vehicleRateAndProtections}
          data-testid="individual-costs-items"
          isCheckoutMobile={isCheckoutMobile}
          isAvisFirst={isAvisFirst}
          gsoAvisFirstTitle={
            isAvisFirst ? t(i18nKeys.gsoAvisFirstTitle) : undefined
          }
          afeTranslationsTitle={
            isAvisFirst ? t(i18nKeys.afeTranslationsTitle) : undefined
          }
          loading={pricingDataLoading}
        />
      </MuiAccordion>
      {!ancillariesCostList?.length && !isAvisFirst && (
        <AbgTypography
          component="p"
          variant="bodySmallRegular"
          sx={carInfoAccordionStyling?.subText}
        >
          {t(i18nKeys.noProtectionsAndAddons)}
        </AbgTypography>
      )}
      {!!pricingData?.amazonCashBack &&
        isAmazonCashbackApply &&
        !hideAmazonBanner && (
          <AmazonBanner
            sx={{ mt: 1 }}
            title={(() => {
              const translatedTitle = t(
                'reservationData.amazonCashBack.title',
                {
                  discount: formatMoney(Number(pricingData?.amazonCashBack), {
                    currency: pricingData?.currencyCode ?? '',
                  }),
                },
              );
              return translatedTitle === 'reservationData.amazonCashBack.title'
                ? `You will receive ${formatMoney(
                    Number(pricingData?.amazonCashBack),
                    {
                      currency: pricingData?.currencyCode ?? 'USD',
                    },
                  )} on an Amazon.com Gift Card upon completing your rental!`
                : translatedTitle;
            })()}
            description={(() => {
              const translatedDescription = t(
                'reservationData.amazonCashBack.description',
              );
              return translatedDescription ===
                'reservationData.amazonCashBack.description'
                ? '*Restrictions apply, see Amazon.com/gc-legal'
                : translatedDescription;
            })()}
          />
        )}
      {!isEmea && !isCheckoutMobile && (
        <Divider sx={carInfoAccordionStyling.divider} />
      )}
      {/* Savings and discounts */}
      {!isEmea && (
        <MuiAccordion
          sx={accordionSx}
          // If the accordion doesn't have content to show, disable the click
          className={`${CustomMuiAccordionClass} ${
            hasSavings ? '' : disableSelectionClass
          }`}
          disableGutters
          elevation={MuiAccordionElevation}
          square
          expanded={!!activeSummary[PANELS.THIRD]}
          ref={createRegionAriaReference(t(i18nKeys.savingsAndDiscountsLabel))}
          onChange={onChange(PANELS.THIRD)}
          data-testid="category-expand-button-savings-discounts"
        >
          <CarInfoSummary
            currency={currency}
            testId="rental-summary-savings-discounts"
            hasExpandIcon={hasSavings}
            activeSummary={activeSummary[PANELS.THIRD]}
            headerText={t(i18nKeys.savingsAndDiscountsLabel)}
            recentCost={Number(pricingData?.savings?.totalSavings || 0)}
            carInfoStyling={{
              headerTextStyles: styleParts?.savingsAndTaxes,
              recentCostStyles: isCheckoutMobile
                ? carInfoSummaryStyling.recentCostStyles
                : { color: paletteBase.semanticGreen },
            }}
            {...mobileVariants}
            isCheckoutMobile={isCheckoutMobile}
            hasPricingData={!!pricingData}
          />

          {hasSavings && (
            <CarInfoDetails
              currency={currency}
              costList={savingsList}
              stylePart={styleParts?.savingsAndTaxes}
              data-testid="individual-costs-items"
              isCheckoutMobile={isCheckoutMobile}
            />
          )}
        </MuiAccordion>
      )}

      {/* Taxes and fees */}
      {!isEmea && (
        <MuiAccordion
          sx={accordionSx}
          // If the accordion doesn't have content to show, disable the click
          className={`${CustomMuiAccordionClass} ${
            hasTaxesFeesCostList ? '' : disableSelectionClass
          }`}
          disableGutters
          elevation={MuiAccordionElevation}
          square
          expanded={!!activeSummary[PANELS.FOURTH]}
          ref={createRegionAriaReference(t(i18nKeys.taxesAndFeesLabel))}
          onChange={onChange(PANELS.FOURTH)}
          data-testid="category-expand-button-taxes-fees"
        >
          <CarInfoSummary
            testId="rental-summary-taxes-fees"
            hasExpandIcon={hasTaxesFeesCostList}
            activeSummary={activeSummary[PANELS.FOURTH]}
            headerText={t(i18nKeys.taxesAndFeesLabel)}
            recentCost={pricingData?.totals?.taxAndFreeTotal || 0}
            carInfoStyling={{ headerTextStyles: styleParts?.savingsAndTaxes }}
            currency={currency}
            {...mobileVariants}
            isCheckoutMobile={isCheckoutMobile}
            hasPricingData={!!pricingData}
          />

          {hasTaxesFeesCostList && (
            <CarInfoTaxes
              currency={currency}
              costList={pricingData?.taxAndFeeItems}
              data-testid="individual-costs-items"
              isCheckoutMobile={isCheckoutMobile}
            />
          )}
        </MuiAccordion>
      )}

      {!isEmea && !isCheckoutMobile && <Divider sx={dividerStyling} />}

      {parts?.totalSection?.()}

      {(ancillariesDialogContextAPI?.rateTermsEnabled || showRateTerms) &&
        !hideRateTerms &&
        !isCheckoutMobile && (
          <RateTermsAccordion
            unpricedExtrasNotes={unpricedExtrasNotes}
            rateTerms={pricingData?.rateTerms}
            onChange={onChange}
            activeSummary={activeSummary}
            currency={currency || 'USD'}
            isPayNowChecked={isPayNowChecked}
          />
        )}

      {/* Qantas Points breakdown — points used & balance to pay */}
      {parts?.qantasPointsBreakdownSection?.()}

      {showDiscountSection && !isRestrictedPage && (
        <DiscountSection
          pricingData={pricingData}
          handlePayNowChange={onPayNowChange}
          isCheckoutMobile={isCheckoutMobile || false}
          vehiclePayType={vehiclePayType}
          enableDiscountSection={enableDiscountSection}
        />
      )}

      {Number(pricingData?.totals?.totalPoints) > 0 ? (
        <>
          <Divider sx={dividerStyling} />

          <PointsAccordionSection
            expanded={!!activeSummary[PANELS.POINTS]}
            onChange={onChange(PANELS.POINTS)}
            i18nForPoints={[
              t(i18nKeys.appliedPreferredPointsHeader),
              t(i18nKeys.appliedPointsText),
            ]}
            appliedPoints={pricingData?.totals?.totalPoints}
            lineItems={pointsItem}
            panelName="applied-points"
          />
        </>
      ) : null}
      {/* Qantas Points Banner */}
      {showQantasPointsBannerInRateTermsCheckout && qantasPoints > 0 && (
        <Box sx={qantasBannerContainer}>
          <Box sx={{ paddingTop: '4px' }}>
            <AemImage
              data-testid="qantas-total-rate-image"
              src={frequentFlyerImage}
              alt="vehicle image"
              width={isMobile ? 72 : 80}
              height={isMobile ? 16 : 18}
              // fallbackSrc={vehicleOnErrorImage}
            />
          </Box>

          <Box>
            <Box>
              <AbgTypography variant="bodyRegularBold" sx={qantasBannerText}>
                {t(
                  `global.fields.checkoutPage.totalRateSummary.qantasPointsDisclaimer`,
                  {
                    qantasPoints: qantasPoints?.toString() || '',
                  },
                )}
              </AbgTypography>
            </Box>
            <Box>
              <AbgTypography
                variant="bodySmallRegular"
                sx={qantasBannerSubText}
              >
                *Conditions apply
              </AbgTypography>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
